import { ApolloClient } from '@apollo/client/core'
import PaginationParams from '@/services/injectables/gql/UtilsGql'
import findContacts from '@/graphql/contacts/findContacts.gql'
import findContactTags from '@/graphql/contacts/findContactTags.gql'
import findIntegrationsCandidates from '@/graphql/contacts/findIntegrationsCandidates.gql'
import deleteContactTag from '@/graphql/contacts/deleteContactTag.gql'
import type { ApolloContext } from '@/services/types/Gql'
import type { ContactTag } from '@/types/generated'
import type { IContactType, ISingleContactType, IRemoteCandidateConnection, IRemoteCandidateConnectionResult } from '@/services/types/Contact'

interface IContactGql {
  $apollo: ApolloClient<any>
  find: (tenantId: string, phoneNumberId: string, idFilter: Array<string | null>, paginationParams: PaginationParams) => Promise<{ results: Array<ISingleContactType> }>
  findTagsByText: (tenantId: string, phoneNumberId: string, text: string) => Promise<ContactTag>
  findIntegrationContacts: (tenantId: string, phoneNumber: string, contactName: string, paginationParams: PaginationParams) => Promise<IRemoteCandidateConnectionResult>
  deleteContactTag: (tenantId: string, phoneNumberId: string, tagValue: string) => Promise<{ success: boolean, errors: Array<string> }>
}

export default class ContactGql implements IContactGql {
  $apollo: ApolloClient<any>

  constructor (context: ApolloContext) {
    this.$apollo = context.$apollo
  }

  async find (tenantId: string, phoneNumberId: string, idFilter: Array<string | null>, paginationParams: PaginationParams) {
    const { data: { contacts } }: { data: { contacts: IContactType } } = await this.$apollo.query({
      query: findContacts,
      fetchPolicy: 'no-cache',
      variables: {
        tenantId,
        phoneNumberId,
        idFilter,
        ...paginationParams
      }
    })
    const fixedContacts = contacts.results?.map((contact: ISingleContactType | null) => ({
      ...contact,
      tags: contact?.tags?.map((tag: any) => tag?.prefixedValue)
    }))
    return {
      ...contacts,
      results: fixedContacts
    } as { results: Array<ISingleContactType> }
  }

  async findTagsByText (tenantId: string, phoneNumberId: string, text: string, entryPoint = 'user') {
    const { data: { contactTags } }: { data: { contactTags: ContactTag } } = await this.$apollo.query({
      query: findContactTags,
      variables: {
        tenantId,
        phoneNumberId,
        text2Search: text,
        entryPoint
      }
    })
    return contactTags
  }

  async findIntegrationContacts (tenantId: string, phoneNumber: string, contactName: string, paginationParams: PaginationParams) {
    const { data: { remoteCandidates } }: { data: { remoteCandidates: IRemoteCandidateConnection } } = await this.$apollo.query({
      query: findIntegrationsCandidates,
      variables: {
        tenantId,
        phoneNumber,
        contactName,
        ...paginationParams
      }
    })
    return { results: remoteCandidates }
  }

  async deleteContactTag (tenantId: string, phoneNumberId: string, tagValue: string) {
    const result = await this.$apollo.mutate({
      mutation: deleteContactTag,
      variables: {
        tenantId,
        phoneNumberId,
        tagValue
      }
    })
    return result.data
  }
}
